import Fuse from 'fuse.js';
import {inject, injectable} from 'inversify';
import {makeAutoObservable} from 'mobx';

import type {Destructible} from '../../../../utils/store';
import type {StringKeysOnly} from '../../../../utils/utility-types';
import type {DataFilterState} from '../../models/DataFilterState';
import type {DataFilterStore} from '../DataFilter/DataFilter.store';

import {FlagManager} from '../../../../utils/store';
import {DataFilterLabelsStore} from '../DataFilterLabels/DataFilterLabels.store';

@injectable()
export class DataFilterSearchManagerStore<Data = any> implements Destructible {
  isReady = new FlagManager();
  keysToSearch: StringKeysOnly<Data>[] = [];
  private store: DataFilterStore<Data> | null = null;

  constructor(
    @inject(DataFilterLabelsStore) readonly labels: DataFilterLabelsStore,
  ) {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  init(
    config: DataFilterStore.SearchConfig<Data>,
    store: DataFilterStore<Data>,
  ) {
    this.store = store;
    this.keysToSearch = [...config.keysToSearch];
    this.isReady.setTrue();
  }

  destruct() {}

  get isLoading() {
    return Boolean(this.store?.isLoading);
  }

  get value() {
    return this.store?.state.search || '';
  }

  clear() {
    this.store?.setState(this.store.state.updateSearch(null));
  }

  filterData(data: Data[], state: DataFilterStore.State): Data[] {
    const term = state.search?.toLowerCase();

    if (!term) {
      return data;
    }

    const fuse = new Fuse(data, {
      keys: this.keysToSearch,
      shouldSort: false,
      threshold: 0.3,
    });

    return fuse.search(term).map(item => item.item);
  }

  onChange(value: DataFilterState.Search) {
    const shouldClear = value == null || value === '';
    const shouldSkipShortInput = (value?.length || 0) < 2;
    const shouldSkipSameValue = this.store?.state.search === value;

    switch (true) {
      case shouldClear:
        return this.clear();
      case this.store == null:
      case shouldSkipShortInput:
      case shouldSkipSameValue:
        return;
      default:
        return this.store.setState(this.store.state.updateSearch(value));
    }
  }
}
